import { EntitiesTypes, EntityTypes } from '@email-editor/ui'
import React, { useState } from 'react'
import { useDragLayer } from 'react-dnd'
import { useDispatch } from 'react-redux'
import { twMerge } from 'tailwind-merge'
import { managementSelectors, useManagement } from '../../../store'
import { copyEntity, removeEntity } from '../../../store/email/actions'
import * as managementActions from '../../../store/management/actions'
import AddEntity from './AddEntity'
import EntityMenu from './EntityMenu'
import EntityMover from './EntityMover'

type BaseEntityPropsType = {
  entity: EntitiesTypes
  children: React.ReactNode
  position: number
  parentId: string
  marginTop?: number
  marginLeft?: number
  marginBottom?: number
  marginRight?: number
  className?: string
}

const getHoverOutline = (entityType: EntityTypes) => {
  switch (entityType) {
    case EntityTypes.Section:
      return 'outline outline-1 outline-[#56C098]'
    default:
      return 'outline outline-1 outline-[#F78828]'
  }
}

const getEntityColor = (entityType: EntityTypes) => {
  switch (entityType) {
    case EntityTypes.Section:
      return '#56C098'
    default:
      return '#F78828'
  }
}

function BaseEntity({
  entity,
  children,
  position,
  parentId,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  className,
}: BaseEntityPropsType) {
  const [showMenu, setShowMenu] = useState(false)
  const dispatch = useDispatch()
  const { isSomethingDragging } = useDragLayer(monitor => ({
    isSomethingDragging: monitor.isDragging(),
  }))
  const isEditing = useManagement(state => managementSelectors.isEditing(state, entity.id))

  const isSomethingEditing = useManagement(state => managementSelectors.isSomethingEditing(state))

  function hover(e: React.SyntheticEvent) {
    if (isSomethingEditing) {
      e.stopPropagation()
      return
    }
    setShowMenu(true)
  }

  function unHover() {
    setShowMenu(false)
  }

  const showHoverColor = showMenu && !isSomethingEditing

  function remove() {
    dispatch(removeEntity(entity))
  }

  function copy(e: React.SyntheticEvent) {
    e.stopPropagation()
    dispatch(copyEntity(entity.id))
  }

  function showPopupLibrary(position: number) {
    return function (event: React.SyntheticEvent) {
      event.stopPropagation()
      dispatch(managementActions.setInsertToForPopupLibrary(parentId, position))
    }
  }

  function enableSettings(event: React.SyntheticEvent) {
    if (event.target) {
      if ((event.target as Element).tagName !== 'INPUT') {
        event.preventDefault()
      }
    }
    event.stopPropagation()
    if (!isSomethingEditing) {
      dispatch(managementActions.setEditingEntityId(entity.id))
    }
  }

  return (
    <tbody>
      <tr>
        <td
          className={twMerge(
            'relative',
            showHoverColor && getHoverOutline(entity.type),
            isSomethingDragging && 'outline-fill-primary outline-dashed outline-1',
            isEditing && 'outline-brand-primary outline',
            className,
          )}
          onMouseEnter={hover}
          onMouseLeave={unHover}
          onClick={enableSettings}
          style={{
            marginTop: marginTop,
            marginRight: marginRight,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
          }}
          id={`entity-${entity.id}`}
        >
          {/*<tbody style={{ display: 'contents' }}>*/}
          {/* tbody breaks image align, but the absence of it throw warnings */}
          {showMenu && !isSomethingEditing && (
            <>
              <AddEntity
                backgroundColor={getEntityColor(entity.type)}
                position={'top'}
                handleClick={showPopupLibrary(position)}
              />
              <EntityMenu
                backgroundColor={getEntityColor(entity.type)}
                toggleSettings={enableSettings}
                remove={remove}
                copy={copy}
              />
              <EntityMover
                backgroundColor={getEntityColor(entity.type)}
                type={entity.type}
                id={entity.id}
                parentId={parentId}
                position={position}
              />
              <AddEntity
                backgroundColor={getEntityColor(entity.type)}
                position={'bottom'}
                handleClick={showPopupLibrary(position + 1)}
              />
            </>
          )}
          {children}
          {/*</tbody>*/}
        </td>
      </tr>
    </tbody>
  )
}

export default BaseEntity
